import { useCallback, useReducer } from 'react';

import useMfa from 'api/useMfa';
import { EToasterStatus, showToast } from 'utils/showToast';

import { resolveCooldownEndsAtMs } from 'components/mfa/cooldown.utils';

import {
  ELockoutType,
  EMfaActionType,
  LockoutState,
  MfaErrorResponse
} from '../LoginModal.types';

import { initialMfaState, mfaReducer } from './mfa-reducer';

interface UseMfaFlowProps {
  onLoginSuccess: (response: any, rememberMe: boolean) => void;
  onRateLimitError: (error: any, lockoutType: ELockoutType) => boolean;
}

export const useMfaFlow = ({ onLoginSuccess, onRateLimitError }: UseMfaFlowProps) => {
  const [mfaState, dispatch] = useReducer(mfaReducer, initialMfaState);
  const { verifyMfa, resendMfa } = useMfa();

  const handleMfaVerify = useCallback(
    async (code: string): Promise<boolean> => {
      if (!mfaState.session.mfaSessionToken) {
        showToast({
          message: 'Invalid session. Please log in again.',
          status: EToasterStatus.ERROR
        });
        return false;
      }

      try {
        const response = await verifyMfa.mutateAsync({
          mfaSessionToken: mfaState.session.mfaSessionToken,
          code
        });

        console.log('response-MFA', response);

        onLoginSuccess(response, mfaState.session.rememberMe);
        return true;
      } catch (error: any) {
        if (onRateLimitError(error, ELockoutType.OTP)) {
          return false;
        }

        if (error?.response?.status === 401) {
          const errorData = error.response.data as MfaErrorResponse;
          const newCurrentAttempt = errorData.maxAttempts - errorData.attemptsRemaining;
          dispatch({
            type: EMfaActionType.UPDATE_ATTEMPTS,
            payload: {
              current: newCurrentAttempt,
              max: errorData.maxAttempts
            }
          });
        }
        return false;
      }
    },
    [
      mfaState.session.mfaSessionToken,
      mfaState.session.rememberMe,
      verifyMfa,
      onLoginSuccess,
      onRateLimitError
    ]
  );

  const handleMfaResend = useCallback(async (): Promise<void> => {
    if (!mfaState.session.mfaSessionToken) {
      showToast({
        message: 'Invalid session. Please log in again.',
        status: EToasterStatus.ERROR
      });
      return;
    }

    try {
      const response = await resendMfa.mutateAsync({
        mfaSessionToken: mfaState.session.mfaSessionToken,
        email: mfaState.session.email
      });

      dispatch({
        type: EMfaActionType.RESEND_SUCCEEDED,
        payload: {
          mfaSessionToken: response.mfaSessionToken,
          cooldownEndsAtMs: resolveCooldownEndsAtMs(response.nextResendAvailableAt)
        }
      });
    } catch (error: any) {
      if (!onRateLimitError(error, ELockoutType.OTP)) {
        showToast({
          message: 'Failed to resend code. Please try again.',
          status: EToasterStatus.ERROR
        });
      }
    }
  }, [
    mfaState.session.mfaSessionToken,
    mfaState.session.email,
    resendMfa,
    onRateLimitError
  ]);

  const setMfaSession = useCallback(
    (sessionData: {
      email: string;
      password: string;
      rememberMe: boolean;
      mfaSessionToken: string;
      maxAttempts: number;
      currentAttempt: number;
      nextResendAvailableAt?: string;
    }): void => {
      dispatch({
        type: EMfaActionType.SET_SESSION,
        payload: {
          email: sessionData.email,
          password: sessionData.password,
          rememberMe: sessionData.rememberMe,
          mfaSessionToken: sessionData.mfaSessionToken,
          maxAttempts: sessionData.maxAttempts,
          currentAttempt: sessionData.currentAttempt,
          cooldownEndsAtMs: resolveCooldownEndsAtMs(sessionData.nextResendAvailableAt)
        }
      });
    },
    []
  );

  const setLockout = useCallback((lockout: LockoutState): void => {
    if (lockout.endTime && lockout.type) {
      dispatch({ type: EMfaActionType.SET_LOCKOUT, payload: { endTime: lockout.endTime, type: lockout.type } });
    } else {
      dispatch({ type: EMfaActionType.CLEAR_LOCKOUT });
    }
  }, []);

  return {
    mfaState,
    handleMfaVerify,
    handleMfaResend,
    setMfaSession,
    setLockout,
    isLoading: verifyMfa.isLoading
  };
};
